/**
 * The one place a Frappe file path becomes a URL the browser may fetch.
 *
 * ---- Why this is not just `apiBase() + file_url` ----
 *
 * `mail.get_email` hands back `attachments[].file_url` as a path — `/private/files/list.xlsx`.
 * Pointing a `fetch` at that path directly works in dev and fails in production, for a reason
 * neither the status code nor the CORS config makes obvious:
 *
 *   - Frappe checks permission on a private file and then answers with an `X-Accel-Redirect`.
 *     **nginx** serves the bytes from that internal location, and Frappe's CORS headers are not on
 *     that response. Measured against `rfqdev.etoserp.com`, 2026-08-12:
 *
 *       GET /private/files/image007.jpg   authorized → 200, Server: nginx, NO Access-Control-*
 *       GET /private/files/image007.jpg   forbidden  → 403, Access-Control-Allow-Origin present
 *
 *     The failing case is the SUCCESSFUL one — which is why probing the route with an unauthorized
 *     curl (the obvious check) reports healthy CORS and proves nothing. The browser gets a valid
 *     200 it is not allowed to read, and `fetch` rejects with a bare TypeError.
 *   - Dev never saw it: `vite.config.ts` proxies `/private/files` server-side with an admin token,
 *     and same-origin requests have no CORS to fail.
 *
 * `frappe.core.doctype.file.file.download_file` returns the SAME bytes (verified byte-identical by
 * md5 on both a private jpg and a public xlsx) through Frappe's own response path, so the CORS
 * headers this deploy already sets do apply. It is permission-checked exactly like the direct
 * route, so this buys the fix without widening what a signed-in user can reach — the alternative,
 * adding `Access-Control-Allow-Origin` to nginx's `/protected/` location, needs a backend change
 * and gets the header onto a route no permission check of ours can see.
 *
 * Public `/files/…` paths go the same way: nginx serves those directly too, so they have the same
 * missing-header problem the moment anything fetches one.
 *
 * ---- What this does NOT need to cover ----
 *
 * A plain `<a href>` download or a top-level navigation is not subject to CORS, so those worked
 * before this and work after. They route through here anyway: one URL per file keeps the download
 * link and the previewed bytes provably the same object, and `download_file` sets
 * `Content-Disposition: attachment; filename=…`, which is a better download than the raw path gave
 * (the `download` attribute is ignored cross-origin).
 */

import { apiBase } from './apiBase'

/** Frappe core's whitelisted file reader. Confirmed present on `rfqdev.etoserp.com` 2026-08-12;
 * `frappe.utils.file_manager.download_file` is the older alias for the same thing. */
export const FILE_DOWNLOAD_METHOD = '/api/method/frappe.core.doctype.file.file.download_file'

/**
 * A fetchable, CORS-readable URL for a Frappe `file_url` path — absolute in a cross-origin deploy,
 * relative in dev (where `apiBase()` is empty and Vite proxies `/api`).
 *
 * Returns `null` for an empty path so callers keep their existing "no file location was returned
 * for this attachment" branch. An already-absolute URL is returned untouched: it points at some
 * other host, where this method does not exist and our session means nothing.
 */
export function fileHref(fileUrl: string): string | null {
  const path = fileUrl.trim()
  if (path === '') return null
  if (/^https?:\/\//i.test(path)) return path
  return `${apiBase()}${FILE_DOWNLOAD_METHOD}?file_url=${encodeURIComponent(path)}`
}
